import React, { useRef, useState } from 'react';
import { DropdownOptions, FieldControllerTypes, FieldTypes } from '../../form_v2/types';
import { IEditFrontOfficeValues, IFrontOfficeFormValuesResp } from './Types';
import * as Yup from 'yup';
import { isSubmitEnabled, maxValidationString, minValidationString, requiredField } from '../../../lib/helperFuncs';
import VideocamIcon from '@mui/icons-material/Videocam';
import PhoneIcon from '@mui/icons-material/Phone';
import MovieIcon from '@mui/icons-material/Movie';
import { Form, Formik } from 'formik';
import FormLayout from '../../layout/Form/FormLayout';
import { VerticalStepper } from '../../Stepper';
import FormActionButtonLayout from '../../layout/Form/FormActionButtonLayout';
import { Button } from '@mui/material';
import { formInStepperBuilder } from '../../form_v2/helpers';
import DialogAction from '../../Dialog/DialogAction';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import HttpService from '../../../services/HttpService';
import { useTranslation } from 'react-i18next';
import { matchIsValidTel } from 'mui-tel-input';
import AsyncButton from '../../AsyncButton/AsyncButton';

const FrontOfficeFormSteps = ({ initialValues }: IEditFrontOfficeValues) => {
  const formRef = useRef(null);
  const {t} = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { enqueueSnackbar } = useSnackbar();
  const [openSubmit, setOpenSubmit] = useState<boolean>(false);
  const [openCancel, setOpenCancel] = useState<boolean>(false);
  const selectOptionsStatus: DropdownOptions[] = [
    { key: t('Ενεργή Διεύθυνση'), value: true },
    { key: t('Ανενεργή Διεύθυνση'), value: false },
  ];
  const navigate = useNavigate();

  const validationSchema = Yup.object({
    // step 1
    title:
      Yup.string().required(requiredField)
        .min(1, minValidationString(1)).max(100, maxValidationString(100)),
    // step 2
    email: Yup.string().required(requiredField).email('Μη αποδεκτή μορφή Email')
      .matches(/^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/, 'Μη αποδεκτή μορφή Email'),
    phone: Yup.string()
      .required(requiredField)
      .test('phone_number', 'Παρακαλώ προσθέστε έγκυρη μορφή τηλεφώνου', (value) => matchIsValidTel(value || '')),
  });

  const step1Forms = [
    {
      control: FieldControllerTypes.input,
      type: FieldTypes.text,
      label: t('Όνομα'),
      name: 'title',
      required: true
    }
  ];

  const step2Forms = [
    {
      control: FieldControllerTypes.input,
      type: FieldTypes.text,
      label: 'Email',
      name: 'email',
      required: true,
    },
    {
      control: FieldControllerTypes.telInput,
      label: t('Τηλέφωνο'),
      name: 'phone',
      required: true,
    }
  ];

  const step3Forms = [
    {
      control: FieldControllerTypes.select,
      type: FieldTypes.select,
      label: t('Κατάσταση'),
      name: 'is_enabled',
      options: selectOptionsStatus,
      disabled: true,
      info: true,
      tooltip: t('Για να μπορεί μια Διεύθυνση ΕΚΟΜΕ να γίνει Ενεργή θα πρέπει να έχει οριστεί τουλάχιστον ένας ΕΚΟΜΕ Publisher γι΄αυτήν.')
    }
  ];

  const step1 = {
    label: t('Στοιχεία Οργανικής Μονάδας'),
    icon: <VideocamIcon />,
    content: step1Forms
  };
  const step2 = {
    label: t('Στοιχεία Επικοινωνίας'),
    icon: <PhoneIcon />,
    content: step2Forms
  };
  const step3 = {
    label: t('Χαρακτηριστικά Οργανικής Μονάδας'),
    icon: <MovieIcon />,
    content: step3Forms
  };

  const wholeForm = [
    { ...step1 },
    { ...step2 },
    { ...step3 },
  ];

  const onSubmit = async (values: IFrontOfficeFormValuesResp) => {
    setIsLoading(true);
    try {
      const resp = await HttpService.getAxiosClient().patch(`${process.env.REACT_APP_BACKEND_URL}/registrations/front_office/${initialValues.id}`, values);
      if (resp.status === 200) {
        enqueueSnackbar('Η Επεξέργασία Διεύθυνσης ΕΚΟΜΕ έγινε επιτυχώς!', { variant: 'success' });
        navigate('/admin/units/dashboard');
      }
    } catch (error) {
      enqueueSnackbar('Η Επεξεργασία Διεύθυνσης ΕΚΟΜΕ απέτυχε, παρακαλώ προσπαθήστε ξανά ή επικοινωνήστε με τον διαχειριστή σας!', { variant: 'error' });
      console.error(error);
    }
    setIsLoading(false);
  };
  
  return (
    <>
      <Formik
        onSubmit={() => undefined}
        initialValues={initialValues}
        validationSchema={validationSchema}
        innerRef={formRef}
        validateOnBlur={false}
      >
        {formik =>
          (<div>
            <Form>
              <FormLayout>
                <VerticalStepper activeStep={1}>
                  {formInStepperBuilder(wholeForm, formik)}
                </VerticalStepper>
              </FormLayout>
              <FormActionButtonLayout>
                <Button variant='outlined' color='error'
                  onClick={() => setOpenCancel(true)}> {t('Ακύρωση')} </Button>
                <AsyncButton
                  title={t('Αποθήκευση')}
                  isLoading={isLoading}
                  disabled={isSubmitEnabled(formik?.errors, formik?.dirty)} 
                  onClick={() => setOpenSubmit(true)}/>
                <DialogAction open={openSubmit} handleClose={() => setOpenSubmit(false)}
                  title={'Επεξεργασία Διεύθυνσης ΕΚΟΜΕ'} 
                  content={'Είστε σίγουροι ότι θέλετε να επεξεργστείτε την συγκεκριμένη διεύθυνση ΕΚΟΜΕ;'}
                  onAccept={() => {
                    setOpenSubmit(false);
                    onSubmit(formik.values);
                  }}/>
                <DialogAction open={openCancel} handleClose={() => setOpenCancel(false)}
                  title={'Ακύρωση Επεξεργασίας Διεύθυνσης EKOME'} content={'Ειστε σιγουρος ότι θέλετε να ακυρώσετε την επεξεργασία Διεύθυνσης EKOME;'}
                  onAccept={() => {
                    setOpenCancel(false); navigate(-1);
                  }}/>
              </FormActionButtonLayout>
            </Form>
          </div>)
        }
      </Formik>
    </>
  );
};

export default FrontOfficeFormSteps;